/**
 * useScreenshotManagement Hook
 * 
 * 管理截圖功能，包括：
 * - 截圖狀態管理
 * - 連拍模式
 * - 圖片管理
 * - 截圖參數設定
 * 
 * @author AI Assistant
 * @date 2025-11-02
 */

import { useState, useCallback, useRef } from 'react';

// 截圖尺寸類型
export type ScreenshotSize = 'original' | '70' | '50' | '1/2' | '1/4' | '1:1';

// 連拍單位類型
export type BurstUnit = 'second' | 'frame';

// 連拍數量類型
export type BurstCount = 3 | 5 | 7;

// 圖片數據接口
export interface ImageData {
  id: string;
  url: string;
  timestamp: number;
  name: string;
}

// Hook 返回類型
export interface UseScreenshotManagementReturn {
  // 狀態
  screenshotOpen: boolean;
  screenshotData: string;
  screenshotName: string;
  screenshotCounter: number;
  screenshotSize: ScreenshotSize;
  burstCount: BurstCount;
  burstInterval: number;
  isBurstMode: boolean;
  isScreenshotMode: boolean;
  capturedImages: ImageData[];
  imageManagerOpen: boolean;
  burstUnit: BurstUnit;

  // 操作函數
  setScreenshotOpen: (open: boolean) => void;
  setScreenshotData: (data: string) => void;
  setScreenshotName: (name: string) => void;
  setScreenshotCounter: (counter: number) => void;
  setScreenshotSize: (size: ScreenshotSize) => void;
  setBurstCount: (count: BurstCount) => void;
  setBurstInterval: (interval: number) => void;
  setIsBurstMode: (mode: boolean) => void;
  setIsScreenshotMode: (mode: boolean) => void;
  setCapturedImages: (images: ImageData[]) => void;
  setImageManagerOpen: (open: boolean) => void;
  setBurstUnit: (unit: BurstUnit) => void;

  // 輔助函數
  incrementScreenshotCounter: () => void;
  clearCapturedImages: () => void;
  addCapturedImage: (image: ImageData) => void;
  removeCapturedImage: (id: string) => void;
  resetScreenshotSettings: () => void;
}

/**
 * 截圖管理 Hook
 * 
 * @returns {UseScreenshotManagementReturn} 截圖管理狀態和操作函數
 * 
 * @example
 * ```typescript
 * const {
 *   screenshotOpen,
 *   setScreenshotOpen,
 *   capturedImages,
 *   addCapturedImage
 * } = useScreenshotManagement();
 * ```
 */
export const useScreenshotManagement = (): UseScreenshotManagementReturn => {
  // ========== 截圖基本狀態 ==========
  const [screenshotOpen, setScreenshotOpen] = useState(false);
  const [screenshotData, setScreenshotData] = useState<string>('');
  const [screenshotName, setScreenshotName] = useState('screenshot');
  const [screenshotCounter, setScreenshotCounter] = useState<number>(1);
  
  // 從 localStorage 讀取截圖尺寸設定
  const [screenshotSize, setScreenshotSize] = useState<ScreenshotSize>(() => {
    const savedSize = localStorage.getItem('screenshotSize');
    return (savedSize as ScreenshotSize) || '1:1';
  });

  // ========== 連拍模式狀態 ==========
  const [burstCount, setBurstCount] = useState<BurstCount>(() => {
    const saved = localStorage.getItem('burstCount');
    return (Number(saved) || 3) as BurstCount;
  });

  const [burstInterval, setBurstInterval] = useState<number>(() => {
    const saved = localStorage.getItem('burstInterval');
    return Number(saved) || 3;
  });

  const [burstUnit, setBurstUnit] = useState<BurstUnit>(() => {
    const saved = localStorage.getItem('burstUnit');
    return (saved as BurstUnit) || 'second';
  });

  const [isBurstMode, setIsBurstMode] = useState(false);
  const [isScreenshotMode, setIsScreenshotMode] = useState(true);

  // ========== 圖片管理狀態 ==========
  const [capturedImages, setCapturedImages] = useState<ImageData[]>([]);
  const [imageManagerOpen, setImageManagerOpen] = useState(false);

  // ========== 輔助函數 ==========
  
  /**
   * 遞增截圖計數器
   */
  const incrementScreenshotCounter = useCallback(() => {
    setScreenshotCounter(prev => prev + 1);
  }, []);

  /**
   * 清空已捕獲的圖片
   */
  const clearCapturedImages = useCallback(() => {
    setCapturedImages([]);
  }, []);

  /**
   * 新增已捕獲的圖片
   */
  const addCapturedImage = useCallback((image: ImageData) => {
    setCapturedImages(prev => [...prev, image]);
  }, []);

  /**
   * 移除已捕獲的圖片
   */
  const removeCapturedImage = useCallback((id: string) => {
    setCapturedImages(prev => prev.filter(img => img.id !== id));
  }, []);

  /**
   * 重置截圖設定為預設值
   */
  const resetScreenshotSettings = useCallback(() => {
    setScreenshotSize('1:1');
    setBurstCount(3);
    setBurstInterval(3);
    setBurstUnit('second');
    setIsBurstMode(false);
    setIsScreenshotMode(true);
    
    // 同時更新 localStorage
    localStorage.setItem('screenshotSize', '1:1');
    localStorage.setItem('burstCount', '3');
    localStorage.setItem('burstInterval', '3');
    localStorage.setItem('burstUnit', 'second');
  }, []);

  // ========== 監聽設定變化並同步到 localStorage ==========
  
  // 當截圖尺寸改變時，保存到 localStorage
  const setScreenshotSizeWithStorage = useCallback((size: ScreenshotSize) => {
    setScreenshotSize(size);
    localStorage.setItem('screenshotSize', size);
  }, []);

  // 當連拍數量改變時，保存到 localStorage
  const setBurstCountWithStorage = useCallback((count: BurstCount) => {
    setBurstCount(count);
    localStorage.setItem('burstCount', count.toString());
  }, []);

  // 當連拍間隔改變時，保存到 localStorage
  const setBurstIntervalWithStorage = useCallback((interval: number) => {
    setBurstInterval(interval);
    localStorage.setItem('burstInterval', interval.toString());
  }, []);

  // 當連拍單位改變時，保存到 localStorage
  const setBurstUnitWithStorage = useCallback((unit: BurstUnit) => {
    setBurstUnit(unit);
    localStorage.setItem('burstUnit', unit);
  }, []);

  // ========== 返回 ==========
  return {
    // 狀態
    screenshotOpen,
    screenshotData,
    screenshotName,
    screenshotCounter,
    screenshotSize,
    burstCount,
    burstInterval,
    isBurstMode,
    isScreenshotMode,
    capturedImages,
    imageManagerOpen,
    burstUnit,

    // 操作函數（使用帶 localStorage 同步的版本）
    setScreenshotOpen,
    setScreenshotData,
    setScreenshotName,
    setScreenshotCounter,
    setScreenshotSize: setScreenshotSizeWithStorage,
    setBurstCount: setBurstCountWithStorage,
    setBurstInterval: setBurstIntervalWithStorage,
    setIsBurstMode,
    setIsScreenshotMode,
    setCapturedImages,
    setImageManagerOpen,
    setBurstUnit: setBurstUnitWithStorage,

    // 輔助函數
    incrementScreenshotCounter,
    clearCapturedImages,
    addCapturedImage,
    removeCapturedImage,
    resetScreenshotSettings,
  };
};

export default useScreenshotManagement;

